import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import {
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography
} from '@mui/material';
import { tagsApi } from '../../services/api';
import { ApiError } from '../../services/api';
import TagTree from './components/TagTree';
import ConfirmationModal from '../common/ConfirmationModal';
import Modal from '../common/Modal';
import EmptyState from '../common/EmptyState';
import LoadingSpinner from '../common/LoadingSpinner';
import { useAlert } from '../../contexts/AlertContext';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  BackButton,
  HeaderButton,
  ControlsContainer
} from '../common/styles';

// Recursive search filtering (pure function, outside component to avoid useMemo dependency warning)
const filterTagsRecursively = (tagsList, term) => {
  if (!term.trim()) return tagsList;
  const lowerTerm = term.toLowerCase();

  return tagsList.reduce((acc, tag) => {
    const nameMatches = tag.name.toLowerCase().includes(lowerTerm);
    const filteredChildren = tag.children
      ? filterTagsRecursively(tag.children, term)
      : [];

    if (nameMatches || filteredChildren.length > 0) {
      acc.push({
        ...tag,
        children: nameMatches ? tag.children : filteredChildren
      });
    }
    return acc;
  }, []);
};

const TagsManagement = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [tagModal, setTagModal] = useState({
    open: false,
    isEdit: false,
    tag: null,
    parentTagId: null
  });
  const [formData, setFormData] = useState({ name: '', description: '', parentTagId: null });
  const [formErrors, setFormErrors] = useState({});

  // Delete state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  useEffect(() => {
    fetchTags();
  }, []);

  // Reset form when modal opens/changes
  useEffect(() => {
    if (tagModal.open) {
      if (tagModal.isEdit && tagModal.tag) {
        setFormData({
          name: tagModal.tag.name || '',
          description: tagModal.tag.description || '',
          parentTagId: tagModal.tag.parentTagId || null
        });
      } else {
        setFormData({
          name: '',
          description: '',
          parentTagId: tagModal.parentTagId || null
        });
      }
      setFormErrors({});
    }
  }, [tagModal.open, tagModal.isEdit, tagModal.tag, tagModal.parentTagId]);

  const fetchTags = async () => {
    try {
      setIsLoading(true);
      const data = await tagsApi.getTagHierarchy();
      setTags(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTags = useMemo(
    () => filterTagsRecursively(tags, searchTerm),
    [tags, searchTerm]
  );

  // Recursive helper to find a tag by ID in the hierarchy
  const findTagById = (tagsList, tagId) => {
    for (const tag of tagsList) {
      if (tag.id === tagId) return tag;
      if (tag.children) {
        const found = findTagById(tag.children, tagId);
        if (found) return found;
      }
    }
    return null;
  };

  // Recursive helper to flatten hierarchy into select options
  const getParentOptions = (tagsList, excludeTagId = null, level = 0) => {
    return tagsList.reduce((acc, tag) => {
      if (tag.id !== excludeTagId) {
        acc.push({
          id: tag.id,
          name: `${'— '.repeat(level)}${tag.name}`
        });
        if (tag.children) {
          acc = acc.concat(getParentOptions(tag.children, excludeTagId, level + 1));
        }
      }
      return acc;
    }, []);
  };

  const closeModal = () => {
    setTagModal({ open: false, isEdit: false, tag: null, parentTagId: null });
  };

  const handleAddTag = async (newTagData) => {
    try {
      const newTag = await tagsApi.createTag(newTagData);
      setTags(prevTags => {
        if (!newTag.parentTagId) {
          return [...prevTags, newTag];
        }
        const addTagToParent = (tagsList) => {
          return tagsList.map(tag => {
            if (tag.id === newTag.parentTagId) {
              return {
                ...tag,
                children: [...(tag.children || []), newTag]
              };
            }
            if (tag.children) {
              return {
                ...tag,
                children: addTagToParent(tag.children)
              };
            }
            return tag;
          });
        };
        return addTagToParent(prevTags);
      });
      closeModal();
      showAlert(`Tag "${newTag.name}" created successfully`, 'success');
    } catch (err) {
      showAlert(err.message || 'Failed to create tag', 'error', err);
    }
  };

  const handleUpdateTag = async (tagId, updatedTagData) => {
    try {
      const updatedTag = await tagsApi.updateTag(tagId, updatedTagData);
      setTags(prevTags => {
        const updateTagInList = (tagsList) => {
          return tagsList.map(tag => {
            if (tag.id === tagId) {
              return updatedTag;
            }
            if (tag.children) {
              return {
                ...tag,
                children: updateTagInList(tag.children)
              };
            }
            return tag;
          });
        };
        return updateTagInList(prevTags);
      });
      closeModal();
      showAlert(`Tag "${updatedTag.name}" updated successfully`, 'success');
    } catch (err) {
      showAlert(err.message || 'Failed to update tag', 'error', err);
    }
  };

  const handleDeleteClick = (tagId) => {
    const tag = findTagById(tags, tagId);
    setTagToDelete(tag);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (tagToDelete) {
      try {
        await tagsApi.deleteTag(tagToDelete.id);
        setTags(prevTags => {
          const removeTag = (tagsList) => {
            return tagsList.filter(tag => {
              if (tag.children) {
                tag.children = removeTag(tag.children);
              }
              return tag.id !== tagToDelete.id;
            });
          };
          return removeTag(prevTags);
        });
        showAlert(`Tag "${tagToDelete.name}" deleted successfully`, 'success');
      } catch (err) {
        let errorMessage = 'Failed to delete tag';

        if (err instanceof ApiError) {
          if (err.status === 400) {
            const { childCount, segmentCount } = err.data;
            if (childCount) {
              errorMessage = `Cannot delete "${tagToDelete.name}". It has ${childCount} child tag${childCount > 1 ? 's' : ''}. Please delete or reassign them first.`;
            } else if (segmentCount) {
              errorMessage = `Cannot delete "${tagToDelete.name}". It is used in ${segmentCount} segment${segmentCount > 1 ? 's' : ''}. Please remove it from all segments first.`;
            } else {
              errorMessage = err.message || errorMessage;
            }
          } else if (err.status === 404) {
            errorMessage = 'Tag not found. It may have been already deleted.';
          }
        }

        showAlert(errorMessage, 'error', err);
      } finally {
        setShowDeleteConfirmation(false);
        setTagToDelete(null);
      }
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Circular reference check for edit mode
    if (tagModal.isEdit && formData.parentTagId) {
      let currentParent = findTagById(tags, formData.parentTagId);
      while (currentParent) {
        if (currentParent.id === tagModal.tag.id) {
          newErrors.parentTagId = 'Cannot create circular reference';
          break;
        }
        currentParent = currentParent.parentTagId
          ? findTagById(tags, currentParent.parentTagId)
          : null;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    if (tagModal.isEdit) {
      await handleUpdateTag(tagModal.tag.id, formData);
    } else {
      await handleAddTag(formData);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Tags Management</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls}>
            <TextField
              placeholder="Search Tags"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{ minWidth: '300px', flex: 1 }}
            />
            <HeaderButton onClick={() => setTagModal({
              open: true,
              isEdit: false,
              tag: null,
              parentTagId: selectedTag?.id || null
            })}>
              <FaPlus /> Add New Tag
            </HeaderButton>
          </ControlsContainer>

          <div style={{
            flex: 1,
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            overflow: 'auto',
            marginTop: showControls ? '24px' : '0',
            transition: 'all 0.3s ease',
            height: 'calc(100vh - 250px)'
          }}>
            {isLoading ? (
              <LoadingSpinner />
            ) : error ? (
              <div>Error: {error}</div>
            ) : filteredTags.length === 0 ? (
              <EmptyState
                title={searchTerm ? 'No Tags Found' : 'No Tags Yet'}
                description={searchTerm
                  ? 'No tags match your search criteria. Try adjusting your search term.'
                  : "There are no tags yet. Click the 'Add New Tag' button to create your first tag."}
              />
            ) : (
              <TagTree
                tags={filteredTags}
                selectedTag={selectedTag}
                onSelectTag={setSelectedTag}
                onDeleteTag={handleDeleteClick}
                onEditTag={(tag) => {
                  setSelectedTag(tag);
                  setTagModal({ open: true, isEdit: true, tag, parentTagId: null });
                }}
              />
            )}
          </div>
        </div>
      </Content>

      <Modal
        isOpen={tagModal.open}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        title={tagModal.isEdit ? 'Edit Tag' : 'Add New Tag'}
        submitText={tagModal.isEdit ? 'Save Changes' : 'Create Tag'}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Tag Name"
            required
            value={formData.name}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, name: e.target.value }));
              if (formErrors.name) setFormErrors(prev => ({ ...prev, name: undefined }));
            }}
            error={!!formErrors.name}
            helperText={formErrors.name}
            fullWidth
            variant="outlined"
            autoComplete="off"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            autoComplete="off"
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>Parent Tag</InputLabel>
            <Select
              value={formData.parentTagId || ''}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, parentTagId: e.target.value || null }));
                if (formErrors.parentTagId) setFormErrors(prev => ({ ...prev, parentTagId: undefined }));
              }}
              label="Parent Tag"
              error={!!formErrors.parentTagId}
            >
              <MenuItem value="">No parent (root level)</MenuItem>
              {getParentOptions(tags, tagModal.isEdit ? tagModal.tag?.id : null).map(option => (
                <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
              ))}
            </Select>
            {formErrors.parentTagId && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {formErrors.parentTagId}
              </Typography>
            )}
          </FormControl>
        </Box>
      </Modal>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setTagToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Tag"
        message={
          `Are you sure you want to delete "${tagToDelete?.name}"? This action cannot be undone.`
        }
      />
    </Container>
  );
};

export default TagsManagement;
